import { html } from 'lit-element';
import { isEmpty } from 'lodash-es';
import ResponseBlocks from '../responseBlocks/index.js';
import HeaderBlocks from '../headerBlocks/index.js';

const mathRenderingModuleUrlDefault =
  'https://cdn.jsdelivr.net/npm/@pie-lib/math-rendering-module@4.1.0/module/index.js';

export const importMathRendering = mathRenderingModuleUrl => {
  if (!window.renderMath) {
    import(mathRenderingModuleUrl || mathRenderingModuleUrlDefault).then(
      ({ _dll_pie_lib__math_rendering: mathRendering }) => {
        // using math rendering accessible package
        window.renderMath = mathRendering.renderMath;

        window.dispatchEvent(new CustomEvent('renderMathReady', {}));
      }
    );
  }
};

export const defineElement = (elementTag, element) => {
  if (!customElements.get(elementTag)) {
    customElements.define(elementTag, element);
  }
};

export const LayoutMethods = {
  ROW: 'row',
  COLUMN: 'column',
};

export const createCorrectnessTag = points => {
  if (points === 1) {
    return 'correct';
  }

  if (points === 0) {
    return 'incorrect';
  }

  if (points > 0 && points < 1) {
    return 'correct-partially';
  }

  return 'unanswered';
};

export const parsePackage = input => {
  const RE_SCOPED = /^(@[^/]+\/[^/@]+)(?:\/([^@]+))?(?:@([\s\S]+))?/;
  const RE_NORMAL = /^([^/@]+)(?:\/([^@]+))?(?:@([\s\S]+))?/;

  if (typeof input !== 'string') {
    throw new TypeError('Expected a string');
  }

  const matched =
    input.charAt(0) === '@' ? input.match(RE_SCOPED) : input.match(RE_NORMAL);

  if (!matched) {
    throw new Error(`[parse-package-name] "${input}" is not a valid string`);
  }

  return {
    name: matched[1],
    path: matched[2] || '',
    version: matched[3] || '',
  };
};

const getElementTag = itemConfig => {
  // this function is supposed to get the proper element tag using the pointed library and set it correctly on the model
  // eg: when elements = { pp-pie-something: @pie-elements/multiple-choice }, proper element tag is "multiple-choice"
  let { elements } = itemConfig || {};
  elements = elements || {};

  if (!elements || isEmpty(elements)) {
    return '';
  }

  // we don't support multi-items now!!!
  const elementMarkupTag = Object.keys(elements)[0];

  const tag = elements[elementMarkupTag];
  const elementPackage = parsePackage(tag || '');
  const { name: elementPackageName = '' } = elementPackage || {};

  return elementPackageName.split('/')[1] || '';
};

export const getItemConfigElement = itemConfig => {
  let { models } = itemConfig || {};
  models = models || [];

  // we don't support multi-items now!!!
  const model = models[0];
  const element = getElementTag(itemConfig);

  if (model) {
    model.element = element;
  }

  return { element, models };
};

export const mapLayout = item => {
  const { config } = item || {};
  const { element: elementTag, models } = getItemConfigElement(config);
  const model = models && models[0];

  switch (elementTag) {
    case 'multiple-choice':
      if (model.choiceMode === 'checkbox') {
        return LayoutMethods.ROW;
      }

      return LayoutMethods.COLUMN;
    default:
      return LayoutMethods.ROW;
  }
};

export const mapResponseBlock = item => {
  const { config } = item || {};
  const { element: elementTag, models } = getItemConfigElement(config);

  let { correctnessGroup } = item || {};
  correctnessGroup = correctnessGroup || '';

  // multi-item
  if (models.length > 1) {
    defineElement(
      'response-block-multi-item',
      ResponseBlocks.ResponseBlockMultiItem
    );

    return response =>
      html`<response-block-multi-item
        .response=${response}
        .correctnessGroup=${correctnessGroup}
      ></response-block-multi-item>`;
  }

  switch (elementTag) {
    case 'multiple-choice': {
      defineElement(
        'response-block-multiple-choice',
        ResponseBlocks.ResponseBlockMultipleChoice
      );

      return response =>
        html`<response-block-multiple-choice
          .response=${response}
          .correctnessGroup=${correctnessGroup}
        ></response-block-multiple-choice>`;
    }
    case 'math-inline': {
      defineElement(
        'response-block-math-inline',
        ResponseBlocks.ResponseBlockMathInline
      );

      return response =>
        html`<response-block-math-inline
          .response=${response}
          .correctnessGroup=${correctnessGroup}
        ></response-block-math-inline>`;
    }
    case 'explicit-constructed-response': {
      defineElement('response-block-ecr', ResponseBlocks.ResponseBlockECR);

      return response =>
        html`<response-block-ecr
          .response=${response}
          .correctnessGroup=${correctnessGroup}
        ></response-block-ecr>`;
    }
    default: {
      defineElement('response-block', ResponseBlocks.ResponseBlock);

      return response =>
        html`<response-block
          .response=${response}
          .correctnessGroup=${correctnessGroup}
        ></response-block>`;
    }
  }
};

export const mapHeaderBlock = item => {
  const { config } = item || {};
  const { element: elementTag, models } = getItemConfigElement(config);

  if (models.length > 1) {
    defineElement('header-block-multi-item', HeaderBlocks.HeaderBlockMultiItem);

    return props => {
      const { session, studentsNumber, studentsAnsweredNr } = props;

      return html`<header-block-multi-item
        .item=${config}
        .session=${session}
        .studentsNumber=${studentsNumber}
        .studentsAnsweredNr=${studentsAnsweredNr}
      ></header-block-multi-item>`;
    };
  }

  switch (elementTag) {
    case 'multiple-choice': {
      defineElement(
        'header-block-multiple-choice',
        HeaderBlocks.HeaderBlockMultipleChoice
      );

      return props => {
        const {
          item: innerItem,
          session,
          studentsNumber,
          studentsAnsweredNr,
        } = props;

        return html`<header-block-multiple-choice
          .item=${innerItem}
          .session=${session}
          .studentsNumber=${studentsNumber}
          .studentsAnsweredNr=${studentsAnsweredNr}
        ></header-block-multiple-choice>`;
      };
    }
    case 'math-inline': {
      defineElement(
        'header-block-math-inline',
        HeaderBlocks.HeaderBlockMathInline
      );

      return props => {
        const {
          item: innerItem,
          session,
          studentsNumber,
          studentsAnsweredNr,
        } = props;

        return html`<header-block-math-inline
          .item=${innerItem}
          .session=${session}
          .studentsNumber=${studentsNumber}
          .studentsAnsweredNr=${studentsAnsweredNr}
        ></header-block-math-inline>`;
      };
    }
    case 'explicit-constructed-response': {
      defineElement('header-block-ecr', HeaderBlocks.HeaderBlockECR);

      return props => {
        const {
          item: innerItem,
          session,
          studentsNumber,
          studentsAnsweredNr,
        } = props;

        return html`<header-block-ecr
          .item=${innerItem}
          .session=${session}
          .studentsNumber=${studentsNumber}
          .studentsAnsweredNr=${studentsAnsweredNr}
        ></header-block-ecr>`;
      };
    }
    default: {
      defineElement('header-block', HeaderBlocks.HeaderBlock);

      return props => {
        const {
          item: innerItem,
          session,
          studentsNumber,
          studentsAnsweredNr,
        } = props;

        return html`<header-block
          .item=${innerItem}
          .session=${session}
          .studentsNumber=${studentsNumber}
          .studentsAnsweredNr=${studentsAnsweredNr}
        ></header-block>`;
      };
    }
  }
};
